import { alpha, Box, Chip, Stack, styled, SxProps, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  ContextLabel,
  IssueDrawerContent,
  IssueIcons,
  ItemDrawerTitle,
  LinkBehavior,
  StatusIcon,
  TaskItemDrawerContent,
  TaskItemIcons,
  useDrawer,
} from "../../../../components";
import { ISSUE_STATUSES, Links, SupportingItemType, TASK_ITEM_STATUSES } from "../../../../constants";
import { useSession } from "../../../../hooks";
import { IssueSummaryDto, queryKeys, TaskItemDto, useRemoveIssueUpvote, useUpvoteIssue } from "../../../../http";

const StyledStack = styled(Stack)<{ $type: SupportingItemType }>`
  cursor: pointer;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0px;

  &:hover {
    background-color: ${({ theme, $type }) =>
      alpha($type === SupportingItemType.TaskItem ? theme.palette.primary.main : theme.palette.orange.main, 0.1)};

    .editMenuIcon {
      opacity: 1;
    }
  }
`;

interface IBaseProps {
  responseAnswerGuid: string;
  onItemClicked?: () => void;
  sx?: SxProps;
}

interface ITaskItemProps extends IBaseProps {
  itemType: SupportingItemType.TaskItem;
  item: TaskItemDto;
}

interface IIssueProps extends IBaseProps {
  itemType: SupportingItemType.Issue;
  item: IssueSummaryDto;
}

type Props = ITaskItemProps | IIssueProps;

const CreatedItemListItem = (props: Props) => {
  const user = useSession();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const queryClient = useQueryClient();
  const { showDrawer } = useDrawer();

  function handleItemClicked() {
    showDrawer({
      title:
        props.itemType === SupportingItemType.TaskItem ? (
          <ItemDrawerTitle link={Links.TaskItemDetail(props.item.guid)} />
        ) : (
          <ItemDrawerTitle link={Links.IssueDetail(props.item.guid)} />
        ),
      content:
        props.itemType === SupportingItemType.TaskItem ? (
          <TaskItemDrawerContent taskItemGuid={props.item.guid} />
        ) : (
          <IssueDrawerContent issueGuid={props.item.guid} />
        ),
    });
    props.onItemClicked?.();
  }

  async function handleUpvoteClicked() {
    if (props.itemType !== SupportingItemType.Issue || props.item === undefined) {
      return;
    }

    if (props.item.upvotes.some((x) => x.userId === user.userId)) {
      removeIssueUpvote({
        issueGuid: props.item.guid,
        teamSlug: props.item.assignedTeam?.slug,
        createdByUserId: props.item.createdByUser.userId,
        userId: user.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: props.item.guid,
        teamSlug: props.item.assignedTeam?.slug,
        createdByUserId: props.item.createdByUser.userId,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
      });
    }

    queryClient.invalidateQueries(queryKeys.surveys.getItemsCreatedFromResponseAnswer(props.responseAnswerGuid));
  }

  return (
    <StyledStack direction="row" onClick={handleItemClicked} sx={{ ...props.sx }} $type={props.itemType}>
      <Stack spacing={0.2} sx={{ py: 0.25, overflow: "hidden", width: "100%" }}>
        {(props.itemType === SupportingItemType.Issue ? props.item.assignedTeam != null : props.item.team != null) && (
          <Box sx={{ pl: 1.25 }}>
            <Chip
              color="primary"
              label={
                props.itemType === SupportingItemType.Issue ? props.item.assignedTeam!.name : props.item.team!.name
              }
              component={LinkBehavior}
              href={Links.TeamImplementation(
                props.itemType === SupportingItemType.Issue ? props.item.assignedTeam!.slug : props.item.team!.slug
              )}
              clickable
            />
          </Box>
        )}

        <Stack spacing={-0.25} sx={{ overflow: "hidden", width: "100%" }}>
          {props.item.context && (
            <ContextLabel context={props.item.context} showIcon hideTeamName condensed sx={{ pl: 1.25 }} />
          )}

          {/* TODO: Task item parent contexts */}

          <Stack spacing={0.5} direction="row" sx={{ alignItems: "center", overflow: "hidden", width: "100%" }}>
            {props.itemType === SupportingItemType.Issue && (
              <StatusIcon
                status={ISSUE_STATUSES.find((x) => x.id === props.item.currentStatus)!}
                sx={{ height: "16px", width: "16px" }}
              />
            )}

            {props.itemType === SupportingItemType.TaskItem && (
              <StatusIcon
                status={TASK_ITEM_STATUSES.find((x) => x.id === props.item.currentStatus)!}
                sx={{ height: "16px", width: "16px" }}
              />
            )}

            <Typography>{props.item.description}</Typography>
          </Stack>
        </Stack>

        {props.itemType === SupportingItemType.Issue && (
          <IssueIcons issue={props.item} onUpvoteClicked={handleUpvoteClicked} sx={{ pl: 1.25, pb: 0.25 }} />
        )}

        {props.itemType === SupportingItemType.TaskItem && (
          <TaskItemIcons taskItem={props.item} sx={{ pl: 1.25, pb: 0.25 }} />
        )}
      </Stack>
    </StyledStack>
  );
};

export { CreatedItemListItem };
